import { ContactUs, CopyrightFooter } from 'app/components'
import { Container, IAppState } from 'app/containers/Container'
import * as React from 'react'
import { Col, Row } from 'react-bootstrap'

interface ILeagueTableFilterOption {
  label: string
  value?: number | string
  id?: number
  code?: string
  sortOrder?: number
}

interface ILeagueTableRow {
  rank: number
  clients: number
  firm: string
  marketShare: number
}

interface ILeagueTableApiResponse {
  filters: {
    selected: {
      year: number | string | null
      assetLevelId: number | null
      state: string | null
    }
    years: ILeagueTableFilterOption[]
    assetLevels: ILeagueTableFilterOption[]
    states: ILeagueTableFilterOption[]
  }
  summary: {
    foundationCount: number
    description: string
  }
  table: ILeagueTableRow[]
}

interface ILeagueTableState {
  filters: {
    years: ILeagueTableFilterOption[]
    assetLevels: ILeagueTableFilterOption[]
    states: ILeagueTableFilterOption[]
  }
  selectedYear: number | string
  selectedAssetLevelId: number | ''
  selectedState: string
  summary: {
    foundationCount: number
    description: string
  }
  rows: ILeagueTableRow[]
  isLoading: boolean
  error: string
}

const API_URL = 'https://9jtty2utj2.execute-api.us-east-1.amazonaws.com/accounting-league-tables'
const MAX_ROWS = 25
const VALID_STATE_CODES = {
  AL: true, AK: true, AZ: true, AR: true, CA: true, CO: true, CT: true, DE: true, DC: true,
  FL: true, GA: true, HI: true, ID: true, IL: true, IN: true, IA: true, KS: true, KY: true,
  LA: true, ME: true, MD: true, MA: true, MI: true, MN: true, MS: true, MO: true, MT: true,
  NE: true, NV: true, NH: true, NJ: true, NM: true, NY: true, NC: true, ND: true, OH: true,
  OK: true, OR: true, PA: true, RI: true, SC: true, SD: true, TN: true, TX: true, UT: true,
  VT: true, VA: true, WA: true, WV: true, WI: true, WY: true,
}
const HIDDEN_YEARS = ['2025', '2026']

const isValidStateOption = (option: ILeagueTableFilterOption) => option.code === null || VALID_STATE_CODES[option.code]
const isVisibleYearOption = (option: ILeagueTableFilterOption) => HIDDEN_YEARS.indexOf(String(option.value || option.label)) === -1

const palette = {
  page: '#f6f8fb',
  card: '#ffffff',
  ink: '#36485c',
  muted: '#708090',
  border: '#d8dee8',
  heading: '#6e7681',
  accent: '#5e1754',
  accentText: '#ffffff',
  tableStripe: '#eef1f5',
  shadow: '0 20px 45px rgba(44, 68, 96, 0.10)',
}

class LeagueTable extends Container<{}, ILeagueTableState> {
  state: ILeagueTableState = {
    filters: {
      years: [],
      assetLevels: [],
      states: [],
    },
    selectedYear: 'mostRecent',
    selectedAssetLevelId: '',
    selectedState: '',
    summary: {
      foundationCount: 0,
      description: '',
    },
    rows: [],
    isLoading: true,
    error: '',
  }

  mapAppStateToContainerState(appState: IAppState): ILeagueTableState {
    return this.state || {
      filters: {
        years: [],
        assetLevels: [],
        states: [],
      },
      selectedYear: 'mostRecent',
      selectedAssetLevelId: '',
      selectedState: '',
      summary: {
        foundationCount: 0,
        description: '',
      },
      rows: [],
      isLoading: true,
      error: '',
    }
  }

  componentDidMount() {
    this.loadLeagueTable()
  }

  buildUrl(year: number | string, assetLevelId: number | '', state: string) {
    const params: string[] = []

    if (year !== '' && year !== null && typeof year !== 'undefined') {
      params.push('year=' + encodeURIComponent(String(year)))
    }

    if (assetLevelId !== '') {
      params.push('assetLevelId=' + encodeURIComponent(String(assetLevelId)))
    }

    if (state) {
      params.push('state=' + encodeURIComponent(state))
    }

    if (params.length === 0) {
      return API_URL
    }

    return API_URL + '?' + params.join('&')
  }

  loadLeagueTable(year?: number | string, assetLevelId?: number | '', state?: string) {
    const nextYear = typeof year !== 'undefined' ? year : this.state.selectedYear
    const nextAssetLevelId = typeof assetLevelId !== 'undefined' ? assetLevelId : this.state.selectedAssetLevelId
    const nextState = typeof state !== 'undefined' ? state : this.state.selectedState

    this.setState({
      isLoading: true,
      error: '',
      selectedYear: nextYear,
      selectedAssetLevelId: nextAssetLevelId,
      selectedState: nextState,
    })

    fetch(this.buildUrl(nextYear, nextAssetLevelId, nextState))
      .then((resp) => resp.json())
      .then((json: ILeagueTableApiResponse) => {
        const years = json.filters && json.filters.years
          ? json.filters.years.filter((option) => isVisibleYearOption(option))
          : []
        const assetLevels = json.filters && json.filters.assetLevels ? json.filters.assetLevels : []
        const states = json.filters && json.filters.states
          ? json.filters.states.filter((option) => isValidStateOption(option))
          : []
        const selected = json.filters && json.filters.selected ? json.filters.selected : { year: 'mostRecent', assetLevelId: null, state: null }
        const rows = json.table ? json.table.slice(0, MAX_ROWS) : []

        this.setState({
          filters: {
            years,
            assetLevels,
            states,
          },
          selectedYear: typeof selected.year === 'undefined' || selected.year === null || HIDDEN_YEARS.indexOf(String(selected.year)) !== -1
            ? 'mostRecent'
            : selected.year,
          selectedAssetLevelId: selected.assetLevelId === null ? '' : selected.assetLevelId,
          selectedState: selected.state && VALID_STATE_CODES[selected.state] ? selected.state : '',
          summary: json.summary || { foundationCount: 0, description: '' },
          rows,
          isLoading: false,
          error: '',
        })
      })
      .catch(() => {
        this.setState({
          isLoading: false,
          error: 'We were unable to load the accounting league table right now.',
        })
      })
  }

  handleYearChange(event) {
    this.loadLeagueTable(event.currentTarget.value, this.state.selectedAssetLevelId, this.state.selectedState)
  }

  handleAssetLevelChange(event) {
    const value = event.currentTarget.value === '' ? '' : Number(event.currentTarget.value)
    this.loadLeagueTable(this.state.selectedYear, value, this.state.selectedState)
  }

  handleStateChange(event) {
    this.loadLeagueTable(this.state.selectedYear, this.state.selectedAssetLevelId, event.currentTarget.value)
  }

  getYearLabel() {
    const selectedYear = this.state.selectedYear
    const years = this.state.filters.years

    for (let i = 0; i < years.length; i++) {
      if (String(years[i].value) === String(selectedYear)) {
        return years[i].label
      }
    }

    return String(selectedYear)
  }

  formatMarketShare(value: number) {
    return value.toFixed(1) + '%'
  }

  formatWholeNumber(value: number) {
    return value.toLocaleString()
  }

  formatSummaryDescription() {
    const description = this.state.summary.description
    const foundationCount = this.state.summary.foundationCount

    if (!description) {
      return ''
    }

    return description.replace(String(foundationCount), foundationCount.toLocaleString())
  }

  renderFilterBlock(title: string, value: number | string, options: ILeagueTableFilterOption[], onChange) {
    return (
      <section
        style={{
          backgroundColor: palette.card,
          border: '1px solid ' + palette.border,
          borderRadius: '20px',
          boxShadow: palette.shadow,
          padding: '22px 22px 24px',
          minHeight: '100%',
        }}
      >
        <h3
          style={{
            color: palette.heading,
            marginTop: 0,
            marginBottom: '16px',
            fontSize: '2.3rem',
            fontWeight: 700,
            textDecoration: 'underline',
            textUnderlineOffset: '6px',
          }}
        >
          {title}
        </h3>
        <select
          value={value}
          onChange={onChange}
          style={{
            width: '100%',
            height: '54px',
            border: '1px solid #aeb8c4',
            borderRadius: '10px',
            padding: '0 14px',
            fontSize: '1.8rem',
            color: palette.ink,
            backgroundColor: '#fff',
          }}
        >
          {options.map((option, index) => {
            const optionValue = typeof option.value !== 'undefined'
              ? option.value
              : typeof option.id !== 'undefined' && option.id !== null
                ? option.id
                : option.code || ''

            return (
              <option key={title + '-' + index + '-' + String(optionValue)} value={optionValue === null ? '' : optionValue}>
                {option.label}
              </option>
            )
          })}
        </select>
      </section>
    )
  }

  renderTable() {
    const rows = this.state.rows

    if (this.state.isLoading) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: palette.muted, fontSize: '1.8rem' }}>
          <i className="fa fa-cog fa-spin" /> Loading league table...
        </div>
      )
    }

    if (this.state.error) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: '#9b2c2c', fontSize: '1.8rem' }}>
          {this.state.error}
        </div>
      )
    }

    if (rows.length === 0) {
      return (
        <div style={{ textAlign: 'center', padding: '40px 20px', color: palette.muted, fontSize: '1.8rem' }}>
          No accounting firms matched the current filters.
        </div>
      )
    }

    return (
      <div style={{ overflowX: 'auto' }}>
        <table
          style={{
            width: '100%',
            borderCollapse: 'separate',
            borderSpacing: 0,
            border: '2px solid #1f2630',
            borderRadius: '16px',
            overflow: 'hidden',
            backgroundColor: palette.card,
          }}
        >
          <thead>
            <tr>
              <th
                colSpan={4}
                style={{
                  backgroundColor: palette.accent,
                  color: palette.accentText,
                  textAlign: 'center',
                  fontSize: '2rem',
                  fontWeight: 700,
                  padding: '14px 10px',
                }}
              >
                {this.getYearLabel()} Market Share - Number of Foundations
              </th>
            </tr>
            <tr style={{ backgroundColor: '#ffffff' }}>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '1.8rem', color: palette.ink, textDecoration: 'underline' }}>Rank</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '1.8rem', color: palette.ink, textDecoration: 'underline' }}>Clients</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontSize: '1.8rem', color: palette.ink, textDecoration: 'underline' }}>Firm</th>
              <th style={{ padding: '12px 16px', textAlign: 'right', fontSize: '1.8rem', color: palette.ink, textDecoration: 'underline' }}>Mkt. Sh.</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={row.rank + '-' + row.firm} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : palette.tableStripe }}>
                <td style={{ padding: '12px 16px', fontSize: '1.8rem', color: palette.ink, width: '90px' }}>{this.formatWholeNumber(row.rank)}</td>
                <td style={{ padding: '12px 16px', fontSize: '1.8rem', color: palette.ink, width: '110px' }}>{this.formatWholeNumber(row.clients)}</td>
                <td style={{ padding: '12px 16px', fontSize: '1.8rem', color: palette.ink, fontWeight: 700 }}>{row.firm}</td>
                <td style={{ padding: '12px 16px', fontSize: '1.8rem', color: palette.ink, textAlign: 'right', width: '120px' }}>{this.formatMarketShare(row.marketShare)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  }

  render() {
    const { filters, selectedYear, selectedAssetLevelId, selectedState } = this.state

    return (
      <section>
        <section
          className="container margin-y-lg animated fadeIn"
          style={{
            overflowX: 'hidden',
            background: 'linear-gradient(180deg, #ffffff 0%, ' + palette.page + ' 100%)',
            borderRadius: '30px',
            padding: '28px 24px 34px',
          }}
        >
          <h1
            style={{
              textAlign: 'center',
              color: palette.heading,
              fontSize: '3.5rem',
              fontWeight: 700,
              marginTop: '6px',
              marginBottom: '34px',
            }}
          >
            Accounting League Tables
          </h1>

          <Row>
            <Col md={4} sm={6} xs={12} style={{ marginBottom: '22px' }}>
              {this.renderFilterBlock('Year', selectedYear, filters.years, this.handleYearChange.bind(this))}
            </Col>
            <Col md={4} sm={6} xs={12} style={{ marginBottom: '22px' }}>
              {this.renderFilterBlock('Foundation Size', selectedAssetLevelId, filters.assetLevels, this.handleAssetLevelChange.bind(this))}
            </Col>
            <Col md={4} sm={12} xs={12} style={{ marginBottom: '22px' }}>
              {this.renderFilterBlock('State', selectedState, filters.states, this.handleStateChange.bind(this))}
            </Col>
          </Row>

          <section
            style={{
              maxWidth: '860px',
              margin: '8px auto 0',
              backgroundColor: palette.card,
              border: '1px solid ' + palette.border,
              borderRadius: '24px',
              boxShadow: palette.shadow,
              padding: '18px',
            }}
          >
            {this.renderTable()}
          </section>

          <p
            style={{
              maxWidth: '860px',
              margin: '22px auto 0',
              color: palette.ink,
              fontSize: '1.9rem',
              lineHeight: '1.5',
              textAlign: 'center',
            }}
          >
            {this.formatSummaryDescription()}
          </p>
        </section>

        <footer>
          <ContactUs />
          <CopyrightFooter />
        </footer>
      </section>
    )
  }
}

export {
  LeagueTable
}



// WEBPACK FOOTER //
// ./src/app/containers/LeagueTable/index.tsx